// import { SocketStock } from "@/helpers/models/stock/socket_stock.model";

export const subTabs = ["All", "Following", "Friends", "Circles"] as const;
export const allTabs = [
  "All",
  "Post",
  "Request",
  "Conversation",
  "System",
] as const;

export type SubTab = (typeof subTabs)[number];
export type MainTab = (typeof allTabs)[number];

export interface NotificationPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface GradientNotification {
  showNotification: boolean;
  notification: Notification;
  timeOut: NodeJS.Timeout;
}

export const MAX_NOTIFICATIONS = 3;
export const NOTIFICATION_HEIGHT = 70;

// Simplified Notification Types based on requirements
export enum NotificationType {
  // Post Notifications
  POST_REGULAR = "post_regular",
  POST_QUOTED = "post_quoted",
  POST_PAGED = "post_paged",
  POST_ANALYSIS_SIGNAL = "post_analysis_signal",
  POST_ANALYSIS_PREDICTION = "post_analysis_prediction",
  POST_ANALYSIS_WATCHLIST = "post_analysis_watchlist",
  POST_ANALYSIS_CHART = "post_analysis_chart",
  POST_PORTFOLIO_TRADE = "post_portfolio_trade",
  POST_PROFILE_HIGHLIGHTS = "post_profile_highlights",
  POST_PROFILE_RELATIONS = "post_profile_relations",
  POST_PORTFOLIO_HIGHLIGHTS = "post_portfolio_highlights",
  POST_NEW_SIGNAL = "post_new_signal",
  POST_COMMENT = "post_comment",
  POST_LIKED = "post_liked",
  POST_TAGGED = "post_tagged",
  POST_REACTION = "post_reaction",
  POST_TAGGED_COMMENT = "post_tagged_comment",
  POST_REPOST = "post_repost",

  // Request Notifications
  REQUEST_FOLLOW = "request_follow",
  REQUEST_FRIEND = "request_friend",
  REQUEST_FRIEND_ACCEPTED = "request_friend_accepted",
  REQUEST_PROFILE_UPDATE = "request_profile_update",
  REQUEST_ACCEPTED = "request_accepted",

  // New Request Notifications for entity operations
  REQUEST_HIGHLIGHT_STATS_ADD = "request_highlightStats_add",
  REQUEST_HIGHLIGHT_STATS_UPDATE = "request_highlightStats_update",
  REQUEST_HIGHLIGHT_STATS_DELETE = "request_highlightStats_delete",

  REQUEST_HIGHLIGHT_STATS_RIGHT_ADD = "request_highlightStatsRight_add",
  REQUEST_HIGHLIGHT_STATS_RIGHT_UPDATE = "request_highlightStatsRight_update",
  REQUEST_HIGHLIGHT_STATS_RIGHT_DELETE = "request_highlightStatsRight_delete",

  REQUEST_TOP_STATS_ADD = "request_topStats_add",
  REQUEST_TOP_STATS_UPDATE = "request_topStats_update",
  REQUEST_TOP_STATS_DELETE = "request_topStats_delete",

  REQUEST_TRADER_TYPE_ADD = "request_traderType_add",
  REQUEST_TRADER_TYPE_UPDATE = "request_traderType_update",
  REQUEST_TRADER_TYPE_DELETE = "request_traderType_delete",

  REQUEST_FAVOURITE_INVESTMENT_ADD = "request_favouriteInvestment_add",
  REQUEST_FAVOURITE_INVESTMENT_UPDATE = "request_favouriteInvestment_update",
  REQUEST_FAVOURITE_INVESTMENT_DELETE = "request_favouriteInvestment_delete",

  REQUEST_RECOMMENDATION_ADD = "request_recommendation_add",
  REQUEST_RECOMMENDATION_UPDATE = "request_recommendation_update",
  REQUEST_RECOMMENDATION_DELETE = "request_recommendation_delete",

  REQUEST_PREDICTIONS_ADD = "request_predictions_add",
  REQUEST_PREDICTIONS_UPDATE = "request_predictions_update",
  REQUEST_PREDICTIONS_DELETE = "request_predictions_delete",

  REQUEST_PROFIT_AND_LOSS_ADD = "request_profitAndLoss_add",
  REQUEST_PROFIT_AND_LOSS_UPDATE = "request_profitAndLoss_update",
  REQUEST_PROFIT_AND_LOSS_DELETE = "request_profitAndLoss_delete",

  REQUEST_WATCHLISTS_ADD = "request_watchlists_add",
  REQUEST_WATCHLISTS_UPDATE = "request_watchlists_update",
  REQUEST_WATCHLISTS_DELETE = "request_watchlists_delete",

  REQUEST_PLAYBOOK_ADD = "request_playbook_add",
  REQUEST_PLAYBOOK_UPDATE = "request_playbook_update",
  REQUEST_PLAYBOOK_DELETE = "request_playbook_delete",

  REQUEST_LINKS_ADD = "request_links_add",
  REQUEST_LINKS_UPDATE = "request_links_update",
  REQUEST_LINKS_DELETE = "request_links_delete",

  REQUEST_FAVOURITE_INFLUENCER_ADD = "request_favouriteInfluencer_add",
  REQUEST_FAVOURITE_INFLUENCER_UPDATE = "request_favouriteInfluencer_update",
  REQUEST_FAVOURITE_INFLUENCER_DELETE = "request_favouriteInfluencer_delete",

  REQUEST_PORTFOLIO_ADD = "request_portfolio_add",
  REQUEST_PORTFOLIO_UPDATE = "request_portfolio_update",
  REQUEST_PORTFOLIO_DELETE = "request_portfolio_delete",

  REQUEST_WINNING_TRADES_ADD = "request_winningTrades_add",
  REQUEST_WINNING_TRADES_UPDATE = "request_winningTrades_update",
  REQUEST_WINNING_TRADES_DELETE = "request_winningTrades_delete",

  REQUEST_IMAGES_ADD = "request_images_add",
  REQUEST_IMAGES_UPDATE = "request_images_update",
  REQUEST_IMAGES_DELETE = "request_images_delete",

  REQUEST_POINTS_ADD = "request_points_add",
  REQUEST_POINTS_UPDATE = "request_points_update",
  REQUEST_POINTS_DELETE = "request_points_delete",

  REQUEST_TRADES_ADD = "request_trades_add",
  REQUEST_TRADES_UPDATE = "request_trades_update",
  REQUEST_TRADES_DELETE = "request_trades_delete",
  REQUEST_SECTION_ADD = "request_sectiontype_add",
  REQUEST_SECTION_UPDATE = "request_sectiontype_update",
  REQUEST_SECTION_DELETE = "request_sectiontype_delete",
  REQUEST_BIO_UPDATE = "request_bio_update",
  REQUEST_BIO_UPDATED = "request_bio_updated",

  // Conversation Notifications
  CONVERSATION_MESSAGE = "conversation_message",
  CONVERSATION_WAITING_REPLY = "conversation_waiting_reply",

  // System Notifications
  SYSTEM_PREDICTION_SUBMITTED = "system_prediction_submitted",
  SYSTEM_PREDICTION_REALIZED = "system_prediction_realized",
  SYSTEM_TRADE_SUBMITTED = "system_trade_submitted",
  SYSTEM_TRADE_EXECUTED = "system_trade_executed",
  SYSTEM_PORTFOLIO_CONNECTED = "system_portfolio_connected",
  SYSTEM_PORTFOLIO_LOADED = "system_portfolio_loaded",
  SYSTEM_NEW_RECOMMENDATION = "system_new_recommendation",
  SYSTEM_BIO_UPDATE = "system_bio_update",
}

// Map notification types to main tabs
export const NotificationTypeToTab: Record<NotificationType, MainTab> = {
  // Post notifications -> Post tab
  [NotificationType.POST_REGULAR]: "Post",
  [NotificationType.POST_QUOTED]: "Post",
  [NotificationType.POST_PAGED]: "Post",
  [NotificationType.POST_ANALYSIS_SIGNAL]: "Post",
  [NotificationType.POST_ANALYSIS_PREDICTION]: "Post",
  [NotificationType.POST_ANALYSIS_WATCHLIST]: "Post",
  [NotificationType.POST_ANALYSIS_CHART]: "Post",
  [NotificationType.POST_PORTFOLIO_TRADE]: "Post",
  [NotificationType.POST_PROFILE_HIGHLIGHTS]: "Post",
  [NotificationType.POST_PROFILE_RELATIONS]: "Post",
  [NotificationType.POST_PORTFOLIO_HIGHLIGHTS]: "Post",
  [NotificationType.POST_NEW_SIGNAL]: "Post",
  [NotificationType.POST_COMMENT]: "Post",
  [NotificationType.POST_TAGGED]: "Post",
  [NotificationType.POST_REACTION]: "Post",
  [NotificationType.POST_TAGGED_COMMENT]: "Post",
  [NotificationType.POST_REPOST]: "Post",
  [NotificationType.POST_LIKED]: "Post",

  // Request notifications -> Request tab
  [NotificationType.REQUEST_FOLLOW]: "Request",
  [NotificationType.REQUEST_FRIEND]: "Request",
  [NotificationType.REQUEST_FRIEND_ACCEPTED]: "Request",
  [NotificationType.REQUEST_ACCEPTED]: "Request",

  //New Request type notifications
  // New Request type notifications -> All tab
  [NotificationType.REQUEST_PROFILE_UPDATE]: "All",
  [NotificationType.REQUEST_SECTION_UPDATE]: "All",
  [NotificationType.REQUEST_SECTION_ADD]: "All",
  [NotificationType.REQUEST_SECTION_DELETE]: "All",
  [NotificationType.REQUEST_HIGHLIGHT_STATS_ADD]: "All",
  [NotificationType.REQUEST_HIGHLIGHT_STATS_UPDATE]: "All",
  [NotificationType.REQUEST_HIGHLIGHT_STATS_DELETE]: "All",
  [NotificationType.REQUEST_HIGHLIGHT_STATS_RIGHT_ADD]: "All",
  [NotificationType.REQUEST_HIGHLIGHT_STATS_RIGHT_UPDATE]: "All",
  [NotificationType.REQUEST_HIGHLIGHT_STATS_RIGHT_DELETE]: "All",
  [NotificationType.REQUEST_TOP_STATS_ADD]: "All",
  [NotificationType.REQUEST_TOP_STATS_UPDATE]: "All",
  [NotificationType.REQUEST_TOP_STATS_DELETE]: "All",
  [NotificationType.REQUEST_TRADER_TYPE_ADD]: "All",
  [NotificationType.REQUEST_TRADER_TYPE_UPDATE]: "All",
  [NotificationType.REQUEST_TRADER_TYPE_DELETE]: "All",
  [NotificationType.REQUEST_FAVOURITE_INVESTMENT_ADD]: "All",
  [NotificationType.REQUEST_FAVOURITE_INVESTMENT_UPDATE]: "All",
  [NotificationType.REQUEST_FAVOURITE_INVESTMENT_DELETE]: "All",
  [NotificationType.REQUEST_RECOMMENDATION_ADD]: "All",
  [NotificationType.REQUEST_RECOMMENDATION_UPDATE]: "All",
  [NotificationType.REQUEST_RECOMMENDATION_DELETE]: "All",
  [NotificationType.REQUEST_PREDICTIONS_ADD]: "All",
  [NotificationType.REQUEST_PREDICTIONS_UPDATE]: "All",
  [NotificationType.REQUEST_PREDICTIONS_DELETE]: "All",
  [NotificationType.REQUEST_PROFIT_AND_LOSS_ADD]: "All",
  [NotificationType.REQUEST_PROFIT_AND_LOSS_UPDATE]: "All",
  [NotificationType.REQUEST_PROFIT_AND_LOSS_DELETE]: "All",
  [NotificationType.REQUEST_WATCHLISTS_ADD]: "All",
  [NotificationType.REQUEST_WATCHLISTS_UPDATE]: "All",
  [NotificationType.REQUEST_WATCHLISTS_DELETE]: "All",
  [NotificationType.REQUEST_PLAYBOOK_ADD]: "All",
  [NotificationType.REQUEST_PLAYBOOK_UPDATE]: "All",
  [NotificationType.REQUEST_PLAYBOOK_DELETE]: "All",
  [NotificationType.REQUEST_LINKS_ADD]: "All",
  [NotificationType.REQUEST_LINKS_UPDATE]: "All",
  [NotificationType.REQUEST_LINKS_DELETE]: "All",
  [NotificationType.REQUEST_FAVOURITE_INFLUENCER_ADD]: "All",
  [NotificationType.REQUEST_FAVOURITE_INFLUENCER_UPDATE]: "All",
  [NotificationType.REQUEST_FAVOURITE_INFLUENCER_DELETE]: "All",
  [NotificationType.REQUEST_PORTFOLIO_ADD]: "All",
  [NotificationType.REQUEST_PORTFOLIO_UPDATE]: "All",
  [NotificationType.REQUEST_PORTFOLIO_DELETE]: "All",
  [NotificationType.REQUEST_WINNING_TRADES_ADD]: "All",
  [NotificationType.REQUEST_WINNING_TRADES_UPDATE]: "All",
  [NotificationType.REQUEST_WINNING_TRADES_DELETE]: "All",
  [NotificationType.REQUEST_IMAGES_ADD]: "All",
  [NotificationType.REQUEST_IMAGES_UPDATE]: "All",
  [NotificationType.REQUEST_IMAGES_DELETE]: "All",
  [NotificationType.REQUEST_POINTS_ADD]: "All",
  [NotificationType.REQUEST_POINTS_UPDATE]: "All",
  [NotificationType.REQUEST_POINTS_DELETE]: "All",
  [NotificationType.REQUEST_TRADES_ADD]: "All",
  [NotificationType.REQUEST_TRADES_UPDATE]: "All",
  [NotificationType.REQUEST_TRADES_DELETE]: "All",
  [NotificationType.REQUEST_BIO_UPDATE]: "All",
  [NotificationType.REQUEST_BIO_UPDATED]: "All",

  // Conversation notifications -> Conversation tab
  [NotificationType.CONVERSATION_MESSAGE]: "Conversation",
  [NotificationType.CONVERSATION_WAITING_REPLY]: "Conversation",

  // System notifications -> System tab
  [NotificationType.SYSTEM_PREDICTION_SUBMITTED]: "System",
  [NotificationType.SYSTEM_PREDICTION_REALIZED]: "System",
  [NotificationType.SYSTEM_TRADE_SUBMITTED]: "System",
  [NotificationType.SYSTEM_TRADE_EXECUTED]: "System",
  [NotificationType.SYSTEM_PORTFOLIO_CONNECTED]: "System",
  [NotificationType.SYSTEM_PORTFOLIO_LOADED]: "System",
  [NotificationType.SYSTEM_NEW_RECOMMENDATION]: "System",
  [NotificationType.SYSTEM_BIO_UPDATE]: "System",
};

// Map notification types to sub-tabs
export const NotificationTypeToSubTab: Record<NotificationType, SubTab> = {
  // Post notifications - all go to "All" sub-tab
  [NotificationType.POST_REGULAR]: "All",
  [NotificationType.POST_QUOTED]: "All",
  [NotificationType.POST_PAGED]: "All",
  [NotificationType.POST_ANALYSIS_SIGNAL]: "All",
  [NotificationType.POST_ANALYSIS_PREDICTION]: "All",
  [NotificationType.POST_ANALYSIS_WATCHLIST]: "All",
  [NotificationType.POST_ANALYSIS_CHART]: "All",
  [NotificationType.POST_PROFILE_HIGHLIGHTS]: "All",
  [NotificationType.POST_PROFILE_RELATIONS]: "All",
  [NotificationType.POST_PORTFOLIO_HIGHLIGHTS]: "All",
  [NotificationType.POST_NEW_SIGNAL]: "All",
  [NotificationType.POST_COMMENT]: "All",
  [NotificationType.POST_TAGGED]: "All",
  [NotificationType.POST_REACTION]: "All",
  [NotificationType.POST_TAGGED_COMMENT]: "All",
  [NotificationType.POST_REPOST]: "All",
  [NotificationType.POST_LIKED]: "All",
  [NotificationType.POST_PORTFOLIO_TRADE]: "All",

  // Request notifications - categorized by relationship type
  [NotificationType.REQUEST_FOLLOW]: "Following",
  [NotificationType.REQUEST_FRIEND]: "Friends",
  [NotificationType.REQUEST_PROFILE_UPDATE]: "Friends",
  [NotificationType.REQUEST_FRIEND_ACCEPTED]: "Friends",
  [NotificationType.REQUEST_ACCEPTED]: "Friends",

  // New Request type notifications - all go to "Profile Updates" sub-tab (or choose appropriate category)
  [NotificationType.REQUEST_HIGHLIGHT_STATS_ADD]: "All",
  [NotificationType.REQUEST_HIGHLIGHT_STATS_UPDATE]: "All",
  [NotificationType.REQUEST_HIGHLIGHT_STATS_DELETE]: "All",
  [NotificationType.REQUEST_HIGHLIGHT_STATS_RIGHT_ADD]: "All",
  [NotificationType.REQUEST_HIGHLIGHT_STATS_RIGHT_UPDATE]: "All",
  [NotificationType.REQUEST_HIGHLIGHT_STATS_RIGHT_DELETE]: "All",
  [NotificationType.REQUEST_TOP_STATS_ADD]: "All",
  [NotificationType.REQUEST_TOP_STATS_UPDATE]: "All",
  [NotificationType.REQUEST_TOP_STATS_DELETE]: "All",
  [NotificationType.REQUEST_TRADER_TYPE_ADD]: "All",
  [NotificationType.REQUEST_TRADER_TYPE_UPDATE]: "All",
  [NotificationType.REQUEST_TRADER_TYPE_DELETE]: "All",
  [NotificationType.REQUEST_FAVOURITE_INVESTMENT_ADD]: "All",
  [NotificationType.REQUEST_FAVOURITE_INVESTMENT_UPDATE]: "All",
  [NotificationType.REQUEST_FAVOURITE_INVESTMENT_DELETE]: "All",
  [NotificationType.REQUEST_RECOMMENDATION_ADD]: "All",
  [NotificationType.REQUEST_RECOMMENDATION_UPDATE]: "All",
  [NotificationType.REQUEST_RECOMMENDATION_DELETE]: "All",
  [NotificationType.REQUEST_PREDICTIONS_ADD]: "All",
  [NotificationType.REQUEST_PREDICTIONS_UPDATE]: "All",
  [NotificationType.REQUEST_PREDICTIONS_DELETE]: "All",
  [NotificationType.REQUEST_PROFIT_AND_LOSS_ADD]: "All",
  [NotificationType.REQUEST_PROFIT_AND_LOSS_UPDATE]: "All",
  [NotificationType.REQUEST_PROFIT_AND_LOSS_DELETE]: "All",
  [NotificationType.REQUEST_WATCHLISTS_ADD]: "All",
  [NotificationType.REQUEST_WATCHLISTS_UPDATE]: "All",
  [NotificationType.REQUEST_WATCHLISTS_DELETE]: "All",
  [NotificationType.REQUEST_PLAYBOOK_ADD]: "All",
  [NotificationType.REQUEST_PLAYBOOK_UPDATE]: "All",
  [NotificationType.REQUEST_PLAYBOOK_DELETE]: "All",
  [NotificationType.REQUEST_LINKS_ADD]: "All",
  [NotificationType.REQUEST_LINKS_UPDATE]: "All",
  [NotificationType.REQUEST_LINKS_DELETE]: "All",
  [NotificationType.REQUEST_FAVOURITE_INFLUENCER_ADD]: "All",
  [NotificationType.REQUEST_FAVOURITE_INFLUENCER_UPDATE]: "All",
  [NotificationType.REQUEST_FAVOURITE_INFLUENCER_DELETE]: "All",
  [NotificationType.REQUEST_PORTFOLIO_ADD]: "All",
  [NotificationType.REQUEST_PORTFOLIO_UPDATE]: "All",
  [NotificationType.REQUEST_PORTFOLIO_DELETE]: "All",
  [NotificationType.REQUEST_WINNING_TRADES_ADD]: "All",
  [NotificationType.REQUEST_WINNING_TRADES_UPDATE]: "All",
  [NotificationType.REQUEST_WINNING_TRADES_DELETE]: "All",
  [NotificationType.REQUEST_IMAGES_ADD]: "All",
  [NotificationType.REQUEST_IMAGES_UPDATE]: "All",
  [NotificationType.REQUEST_IMAGES_DELETE]: "All",
  [NotificationType.REQUEST_POINTS_ADD]: "All",
  [NotificationType.REQUEST_POINTS_UPDATE]: "All",
  [NotificationType.REQUEST_POINTS_DELETE]: "All",
  [NotificationType.REQUEST_TRADES_ADD]: "All",
  [NotificationType.REQUEST_TRADES_UPDATE]: "All",
  [NotificationType.REQUEST_TRADES_DELETE]: "All",
  [NotificationType.REQUEST_SECTION_ADD]: "All",
  [NotificationType.REQUEST_SECTION_UPDATE]: "All",
  [NotificationType.REQUEST_SECTION_DELETE]: "All",
  [NotificationType.REQUEST_BIO_UPDATE]: "All",
  [NotificationType.REQUEST_BIO_UPDATED]: "All",

  // Conversation notifications - categorized by relationship type
  [NotificationType.CONVERSATION_MESSAGE]: "All",
  [NotificationType.CONVERSATION_WAITING_REPLY]: "All",

  // System notifications - all go to "All" sub-tab
  [NotificationType.SYSTEM_PREDICTION_SUBMITTED]: "All",
  [NotificationType.SYSTEM_PREDICTION_REALIZED]: "All",
  [NotificationType.SYSTEM_TRADE_SUBMITTED]: "All",
  [NotificationType.SYSTEM_TRADE_EXECUTED]: "All",
  [NotificationType.SYSTEM_PORTFOLIO_CONNECTED]: "All",
  [NotificationType.SYSTEM_PORTFOLIO_LOADED]: "All",
  [NotificationType.SYSTEM_NEW_RECOMMENDATION]: "All",
  [NotificationType.SYSTEM_BIO_UPDATE]: "All",
};

// Base notification interface
export interface NotificationBase {
  _id: string;
  notificationId: string;
  notificationType: NotificationType;
  notificationNewType: NotificationType;
  title?: string;
  body?: any;
  data: any;
  isRead: boolean;
  isViewed: boolean;
  createdOn: string;
  isOffline?: boolean;
  isDeleted?: boolean;
  userId?: string;
  from_friends: boolean;
  from_follower: boolean;
  from_circle: boolean;
}

// Post notification data structure
export interface PostData {
  _id: string;
  message: string;
  en_message?: string;
  ar_message?: string;
  fullName: string;
  userName: string;
  media: any[];
  profilePicture: number;
  userGender?: string;
  connectionType?: string;
  isDeleted?: boolean;
  createdOn: string;
  symbols?: string[];
  [key: string]: any;
}

// Request notification data structure
export interface RequestData {
  userId: string;
  userName: string;
  fullName: string;
  userFullName: string;
  profilePicture?: number;
  connectionType?: string;
  isDeleted?: boolean;
  message: string;
  userGender?: string;
  createdOn: string;
  symbols?: string[];
  [key: string]: any;
}

// Conversation notification data structure
export interface ConversationData {
  userId: string;
  userFullName: string;
  userProfilePicture: number;
  message: string;
  chatId: string;
  fullName: string;
  userName: string;
  media: any[];
  profilePicture: number;
  userGender?: string;
  isDeleted?: boolean;
  createdOn: string;
  symbols?: string[];
  [key: string]: any;
}

// System notification data structure
export interface SystemData {
  message: string;
  type: string;
  userFullName: string;
  isDeleted?: boolean;
  [key: string]: any;
}

// Specific notification types
export interface PostNotification extends NotificationBase {
  notificationNewType:
    | NotificationType.POST_REGULAR
    | NotificationType.POST_QUOTED
    | NotificationType.POST_PAGED
    | NotificationType.POST_ANALYSIS_SIGNAL
    | NotificationType.POST_ANALYSIS_PREDICTION
    | NotificationType.POST_ANALYSIS_WATCHLIST
    | NotificationType.POST_ANALYSIS_CHART
    | NotificationType.POST_PROFILE_HIGHLIGHTS
    | NotificationType.POST_PORTFOLIO_HIGHLIGHTS
    | NotificationType.POST_PROFILE_RELATIONS
    | NotificationType.POST_NEW_SIGNAL;
  data: PostData;
}

export interface RequestNotification extends NotificationBase {
  notificationNewType:
    | NotificationType.REQUEST_FOLLOW
    | NotificationType.REQUEST_FRIEND
    | NotificationType.REQUEST_PROFILE_UPDATE
    | NotificationType.REQUEST_FRIEND_ACCEPTED;
  data: RequestData;
}

export interface ConversationNotification extends NotificationBase {
  notificationNewType:
    | NotificationType.CONVERSATION_MESSAGE
    | NotificationType.CONVERSATION_WAITING_REPLY;
  data: ConversationData;
}

export interface SystemNotification extends NotificationBase {
  notificationNewType:
    | NotificationType.SYSTEM_PREDICTION_SUBMITTED
    | NotificationType.SYSTEM_PREDICTION_REALIZED
    | NotificationType.SYSTEM_TRADE_SUBMITTED
    | NotificationType.SYSTEM_TRADE_EXECUTED
    | NotificationType.SYSTEM_PORTFOLIO_CONNECTED
    | NotificationType.SYSTEM_PORTFOLIO_LOADED
    | NotificationType.SYSTEM_NEW_RECOMMENDATION;
  data: SystemData;
}

// Union type for all notifications
export type Notification =
  | PostNotification
  | RequestNotification
  | ConversationNotification
  | SystemNotification;

// Type guards
export const isPostNotification = (
  notif: Notification
): notif is PostNotification => {
  const postTypes = [
    NotificationType.POST_REGULAR,
    NotificationType.POST_QUOTED,
    NotificationType.POST_PAGED,
    NotificationType.POST_ANALYSIS_SIGNAL,
    NotificationType.POST_ANALYSIS_PREDICTION,
    NotificationType.POST_ANALYSIS_WATCHLIST,
    NotificationType.POST_ANALYSIS_CHART,
    NotificationType.POST_PROFILE_HIGHLIGHTS,
    NotificationType.POST_PROFILE_RELATIONS,
    NotificationType.POST_PORTFOLIO_HIGHLIGHTS,
    NotificationType.POST_NEW_SIGNAL,
    NotificationType.POST_COMMENT,
    NotificationType.POST_TAGGED,
    NotificationType.POST_REACTION,
    NotificationType.POST_TAGGED_COMMENT,
    NotificationType.POST_REPOST,
    NotificationType.POST_LIKED,
  ];
  return postTypes.includes(notif.notificationNewType);
};
export const isPostNotificationForClick = (
  notif: Notification
): notif is PostNotification => {
  const postTypes = [
    NotificationType.POST_REGULAR,
    NotificationType.POST_QUOTED,
    NotificationType.POST_PAGED,
    NotificationType.POST_ANALYSIS_SIGNAL,
    NotificationType.POST_ANALYSIS_PREDICTION,
    NotificationType.POST_ANALYSIS_WATCHLIST,
    NotificationType.POST_ANALYSIS_CHART,
    NotificationType.POST_PROFILE_HIGHLIGHTS,
    NotificationType.POST_PROFILE_RELATIONS,
    NotificationType.POST_PORTFOLIO_HIGHLIGHTS,
    NotificationType.POST_NEW_SIGNAL,
    NotificationType.POST_COMMENT,
    NotificationType.POST_TAGGED,
    NotificationType.POST_REACTION,
    NotificationType.POST_TAGGED_COMMENT,
    NotificationType.POST_REPOST,
    NotificationType.POST_LIKED,
    NotificationType.POST_PORTFOLIO_TRADE,
    NotificationType.SYSTEM_PREDICTION_REALIZED,
    NotificationType.SYSTEM_PREDICTION_SUBMITTED,
  ];
  return postTypes.includes(notif.notificationNewType);
};

export const isProfileNotificationForClick = (
  notif: Notification
): notif is RequestNotification => {
  const profileTypes = [
    NotificationType.REQUEST_PROFILE_UPDATE,
    NotificationType.REQUEST_BIO_UPDATE,
    NotificationType.REQUEST_BIO_UPDATED,
    NotificationType.REQUEST_RECOMMENDATION_ADD,
    NotificationType.REQUEST_RECOMMENDATION_UPDATE,
    NotificationType.REQUEST_RECOMMENDATION_DELETE,
    NotificationType.REQUEST_TRADES_ADD,
    NotificationType.REQUEST_TRADES_UPDATE,
    NotificationType.REQUEST_TRADES_DELETE,
    NotificationType.REQUEST_POINTS_ADD,
    NotificationType.REQUEST_POINTS_UPDATE,
    NotificationType.REQUEST_POINTS_DELETE,
    NotificationType.REQUEST_IMAGES_ADD,
    NotificationType.REQUEST_IMAGES_UPDATE,
    NotificationType.REQUEST_IMAGES_DELETE,
    NotificationType.REQUEST_WINNING_TRADES_ADD,
    NotificationType.REQUEST_WINNING_TRADES_UPDATE,
    NotificationType.REQUEST_WINNING_TRADES_DELETE,
    NotificationType.REQUEST_PORTFOLIO_ADD,
    NotificationType.REQUEST_PORTFOLIO_UPDATE,
    NotificationType.REQUEST_PORTFOLIO_DELETE,
    NotificationType.REQUEST_FAVOURITE_INFLUENCER_ADD,
    NotificationType.REQUEST_FAVOURITE_INFLUENCER_UPDATE,
    NotificationType.REQUEST_FAVOURITE_INFLUENCER_DELETE,
    NotificationType.REQUEST_LINKS_ADD,
    NotificationType.REQUEST_LINKS_UPDATE,
    NotificationType.REQUEST_LINKS_DELETE,
    NotificationType.REQUEST_PLAYBOOK_ADD,
    NotificationType.REQUEST_PLAYBOOK_UPDATE,
    NotificationType.REQUEST_PLAYBOOK_DELETE,
    NotificationType.REQUEST_WATCHLISTS_ADD,
    NotificationType.REQUEST_WATCHLISTS_UPDATE,
    NotificationType.REQUEST_WATCHLISTS_DELETE,
    NotificationType.REQUEST_PROFIT_AND_LOSS_ADD,
    NotificationType.REQUEST_PROFIT_AND_LOSS_UPDATE,
    NotificationType.REQUEST_PROFIT_AND_LOSS_DELETE,
    NotificationType.REQUEST_PREDICTIONS_ADD,
    NotificationType.REQUEST_PREDICTIONS_UPDATE,
    NotificationType.REQUEST_PREDICTIONS_DELETE,
    NotificationType.REQUEST_FAVOURITE_INVESTMENT_ADD,
    NotificationType.REQUEST_FAVOURITE_INVESTMENT_UPDATE,
    NotificationType.REQUEST_FAVOURITE_INVESTMENT_DELETE,
    NotificationType.REQUEST_TRADER_TYPE_ADD,
    NotificationType.REQUEST_TRADER_TYPE_UPDATE,
    NotificationType.REQUEST_TRADER_TYPE_DELETE,
    NotificationType.REQUEST_TOP_STATS_ADD,
    NotificationType.REQUEST_TOP_STATS_UPDATE,
    NotificationType.REQUEST_TOP_STATS_DELETE,
    NotificationType.REQUEST_HIGHLIGHT_STATS_ADD,
    NotificationType.REQUEST_HIGHLIGHT_STATS_UPDATE,
    NotificationType.REQUEST_HIGHLIGHT_STATS_DELETE,
    NotificationType.REQUEST_HIGHLIGHT_STATS_RIGHT_ADD,
    NotificationType.REQUEST_HIGHLIGHT_STATS_RIGHT_UPDATE,
    NotificationType.REQUEST_HIGHLIGHT_STATS_RIGHT_DELETE,
  ];
  return profileTypes.includes(notif.notificationNewType);
};

export const isPostInteractionNotification = (
  notif: Notification
): notif is PostNotification => {
  const postTypes = [
    NotificationType.POST_COMMENT,
    NotificationType.POST_TAGGED,
    NotificationType.POST_REACTION,
    NotificationType.POST_TAGGED_COMMENT,
    NotificationType.POST_REPOST,
    NotificationType.POST_LIKED,
    NotificationType.SYSTEM_PREDICTION_SUBMITTED,
  ];
  return postTypes.includes(notif.notificationNewType)
    ? notif.data.postId
    : false;
};

export const isRequestNotification = (
  notif: Notification
): notif is RequestNotification => {
  const requestTypes = [
    NotificationType.REQUEST_FOLLOW,
    NotificationType.REQUEST_FRIEND,
    NotificationType.REQUEST_PROFILE_UPDATE,
    NotificationType.REQUEST_FRIEND_ACCEPTED,
  ];
  return requestTypes.includes(notif.notificationNewType);
};

export const isConversationNotification = (
  notif: Notification
): notif is ConversationNotification => {
  const conversationTypes = [
    NotificationType.CONVERSATION_MESSAGE,
    NotificationType.CONVERSATION_WAITING_REPLY,
  ];
  return conversationTypes.includes(notif.notificationNewType);
};

export const isSystemNotification = (
  notif: Notification
): notif is SystemNotification => {
  const systemTypes = [
    NotificationType.SYSTEM_PREDICTION_SUBMITTED,
    NotificationType.SYSTEM_PREDICTION_REALIZED,
    NotificationType.SYSTEM_TRADE_SUBMITTED,
    NotificationType.SYSTEM_TRADE_EXECUTED,
    NotificationType.SYSTEM_PORTFOLIO_CONNECTED,
    NotificationType.SYSTEM_PORTFOLIO_LOADED,
    NotificationType.SYSTEM_NEW_RECOMMENDATION,
  ];
  return systemTypes.includes(notif.notificationNewType);
};

// Helper function to get sub-tab for a notification
export const getNotificationSubTab = (notification: Notification): SubTab => {
  return NotificationTypeToSubTab[notification.notificationNewType];
};

// Counts interface
export interface NotificationCounts {
  // all: number;
  // byTab: Record<MainTab, number>;
  // unreadByTab: Record<MainTab, number>;
  // bySubTab: Record<SubTab, number>;
  // unreadBySubTab: Record<SubTab, number>;

  totalUnreadCount: number;
  unreadByMainTab: Record<MainTab, number>;
  unreadByMainTabSubTab: Record<MainTab, Record<SubTab, number>>;
}
export interface NotificationCurrentTabCounts {
  All: number;
  Following: number;
  Friends: number;
  Circles: number;
}
